import { scheduleBusPreview } from "./audioPreview";
import {
  AUDIO_BUS_LABELS,
  type AudioBus,
  getAudioSettings,
  setAudioBus,
  setAudioSettings,
  subscribeAudioSettings
} from "./audioSettings";
import { getControlSettings, setControlSettings, subscribeControlSettings } from "./controlSettings";
import { GAMEPAD, connectedGamepad, gamepadButtonDown, risingEdge } from "./gamepad";

const BUSES: AudioBus[] = ["master", "music", "sfx", "voice"];

let boundDialog: HTMLDialogElement | null = null;
let boundOpen: (() => void) | null = null;
let boundClose: (() => void) | null = null;

export function isOptionsMenuOpen(): boolean {
  return Boolean(boundDialog?.open);
}

export function openOptionsMenu(): void {
  boundOpen?.();
}

export function closeOptionsMenu(): void {
  boundClose?.();
}

export function toggleOptionsMenu(): void {
  if (isOptionsMenuOpen()) closeOptionsMenu();
  else openOptionsMenu();
}

export function bindAudioOptionsMenu(root: ParentNode = document): () => void {
  const dialog = root.querySelector<HTMLDialogElement>("#options-menu");
  if (!dialog) throw new Error("The options menu is missing.");
  boundDialog = dialog;

  const openers = [...root.querySelectorAll<HTMLButtonElement>("[data-open-options]")];
  const closer = root.querySelector<HTMLButtonElement>("#close-options");
  const sliders = new Map<AudioBus, HTMLInputElement>();
  const readouts = new Map<AudioBus, HTMLElement>();
  const menuTheme = root.querySelector<HTMLInputElement>("#menu-theme");
  const alwaysTouchControls = root.querySelector<HTMLInputElement>("#always-touch-controls");
  const showGameChrome = root.querySelector<HTMLInputElement>("#show-game-chrome");

  for (const bus of BUSES) {
    const slider = root.querySelector<HTMLInputElement>(`#vol-${bus}`);
    const readout = root.querySelector<HTMLElement>(`#vol-${bus}-value`);
    if (!slider || !readout) throw new Error(`The ${AUDIO_BUS_LABELS[bus]} control is missing.`);
    sliders.set(bus, slider);
    readouts.set(bus, readout);
  }

  const paint = (): void => {
    const settings = getAudioSettings();
    for (const bus of BUSES) {
      const percent = Math.round(settings[bus] * 100);
      const slider = sliders.get(bus);
      const readout = readouts.get(bus);
      if (slider && slider.value !== String(percent)) slider.value = String(percent);
      if (readout) readout.textContent = `${percent}`;
    }
    if (menuTheme) menuTheme.checked = settings.menuTheme;
    if (alwaysTouchControls) {
      alwaysTouchControls.checked = getControlSettings().alwaysShowTouchControls;
    }
    if (showGameChrome) {
      showGameChrome.checked = getControlSettings().showGameChrome;
    }
  };

  const open = (): void => {
    if (!dialog.open) dialog.showModal();
    sliders.get("master")?.focus();
  };

  const close = (): void => {
    if (dialog.open) dialog.close();
  };
  boundOpen = open;
  boundClose = close;

  const onSliderInput = (bus: AudioBus, event: Event): void => {
    const value = Number.parseInt((event.target as HTMLInputElement).value, 10) / 100;
    setAudioBus(bus, value);
    if (bus === "sfx" || bus === "voice") scheduleBusPreview(bus);
  };

  paint();
  const unsubscribe = subscribeAudioSettings(paint);
  const unsubscribeControls = subscribeControlSettings(paint);

  const openerHandlers = openers.map((button) => {
    const handler = (): void => open();
    button.addEventListener("click", handler);
    return () => button.removeEventListener("click", handler);
  });

  const onMenuTheme = (): void => {
    if (!menuTheme) return;
    setAudioSettings({ menuTheme: menuTheme.checked });
  };
  menuTheme?.addEventListener("change", onMenuTheme);

  const onAlwaysTouchControls = (): void => {
    if (!alwaysTouchControls) return;
    setControlSettings({ alwaysShowTouchControls: alwaysTouchControls.checked });
  };
  alwaysTouchControls?.addEventListener("change", onAlwaysTouchControls);

  const onShowGameChrome = (): void => {
    if (!showGameChrome) return;
    setControlSettings({ showGameChrome: showGameChrome.checked });
  };
  showGameChrome?.addEventListener("change", onShowGameChrome);

  closer?.addEventListener("click", close);
  dialog.addEventListener("cancel", close);

  let selectDown = false;
  let selectPoll = 0;
  const pollSelect = (): void => {
    const select = risingEdge(selectDown, gamepadButtonDown(connectedGamepad(), GAMEPAD.SELECT));
    selectDown = select.down;
    if (select.pressed) {
      if (dialog.open) close();
      else open();
    }
    selectPoll = requestAnimationFrame(pollSelect);
  };
  selectPoll = requestAnimationFrame(pollSelect);

  const sliderCleanups = BUSES.map((bus) => {
    const slider = sliders.get(bus)!;
    const handler = (event: Event): void => onSliderInput(bus, event);
    slider.addEventListener("input", handler);
    return () => slider.removeEventListener("input", handler);
  });

  return () => {
    unsubscribe();
    unsubscribeControls();
    for (const cleanup of openerHandlers) cleanup();
    closer?.removeEventListener("click", close);
    dialog.removeEventListener("cancel", close);
    menuTheme?.removeEventListener("change", onMenuTheme);
    alwaysTouchControls?.removeEventListener("change", onAlwaysTouchControls);
    showGameChrome?.removeEventListener("change", onShowGameChrome);
    for (const cleanup of sliderCleanups) cleanup();
    cancelAnimationFrame(selectPoll);
    if (boundDialog === dialog) {
      boundDialog = null;
      boundOpen = null;
      boundClose = null;
    }
  };
}
